import { defineMessages } from "@/lib/i18n/messages";

export default defineMessages({
  es: {
    howToPlay: {
      title: "Cómo se juega La palabra del día",
      items: [
        {
          q: "¿Cómo se juega?",
          a: "Escribe una palabra del largo indicado (de 5 a 8 letras, cambia cada día) y presiona Enviar. Verde: la letra está en su lugar. Amarillo: está en la palabra, pero en otro lugar. Gris: no está. Tienes 6 intentos.",
        },
        {
          q: "¿De dónde salen las palabras?",
          a: "De los eventos de la semana en tu país: conciertos, ferias, teatro, cine y más. Al acertar te mostramos en qué eventos aparece la palabra.",
        },
        {
          q: "¿Cuándo cambia la palabra?",
          a: "Todos los días a medianoche de tu país. Hay una sola partida al día, en cualquier idioma.",
        },
        {
          q: "¿Las tildes cuentan?",
          a: "No: se ignoran al jugar y se muestran al final. La Ñ es una letra aparte.",
        },
        {
          q: "¿Hay ranking?",
          a: "Sí, dos: por puntos (acertar en el primer intento da 6 puntos, en el sexto 1) y por tiempo (tu promedio y tu mejor tiempo). Para aparecer necesitas una cuenta y sumarte al ranking; puedes usar un alias.",
        },
      ],
    },
    title: "La palabra del día",
    ogSubtitle: "Adivina la palabra de hoy, sacada de los eventos de tu ciudad.",
    description:
      "Adivina la palabra del día sobre eventos, fiestas y música en 6 intentos. Una palabra nueva cada día en Ciudad Viva.",
    intro: (attempts: number) =>
      `Adivina la palabra de hoy sobre eventos, fiestas y música. Tienes ${attempts} intentos.`,
    legend: {
      correct: "En su lugar",
      present: "Está, en otro lugar",
      absent: "No está",
    },
    accentsNote: "Las tildes no cuentan; la Ñ es una letra aparte.",
    loading: "Cargando la palabra de hoy…",
    loadError: "No pudimos cargar la palabra de hoy.",
    retry: "Reintentar",
    notEnoughLetters: "Faltan letras",
    guessError: "No pudimos revisar tu intento. Inténtalo de nuevo.",
    expired: "La palabra ya cambió: cargamos la de hoy.",
    won: (attempts: number) =>
      attempts === 1 ? "¡Increíble! La adivinaste al primer intento." : `¡Bien! La adivinaste en ${attempts} intentos.`,
    lost: [
      "¡Casi! Sabemos que a la próxima la encuentras.",
      "Hoy se escapó, pero mañana hay una palabra nueva esperándote.",
      "No pasa nada: las mejores rachas empiezan después de un día difícil.",
      "Esta vez no salió. ¡Mañana vas por la revancha!",
      "La de hoy estaba difícil. Mañana tienes una nueva oportunidad.",
    ],
    todayWord: "La palabra de hoy:",
    wordEvents: "La palabra de hoy aparece en estos eventos",
    notInDictionary: "No está en el diccionario",
    streak: (days: number) => `🔥 Racha de ${days} ${days === 1 ? "día" : "días"}`,
    imported: "Pasamos tu partida de hoy a tu cuenta.",
    playedInTitle: (language: string) => `Hoy ya jugaste en ${language}`,
    playedInBody: "Es una palabra al día, en cualquier idioma. Mañana hay otra.",
    playedInLink: (language: string) => `Ver mi partida en ${language}`,
    synced: "Tu partida cambió en otra pestaña: la actualizamos.",
    stats: {
      title: "Tus estadísticas",
      played: "Jugadas",
      winRate: "% victorias",
      currentStreak: "Racha actual",
      maxStreak: "Mejor racha",
      distribution: "Intentos para adivinarla",
    },
    saveStreak: {
      title: "Guarda tu racha",
      body: "Inicia sesión para que tus partidas, rachas y estadísticas queden en tu cuenta, incluida la de hoy.",
      button: "Iniciar sesión",
    },
    nextWord: "Nueva palabra en",
    shareTitle: (puzzle: number) => `La palabra del día #${puzzle}`,
    shareWhatsApp: "Compartir por WhatsApp",
    copyResult: "Copiar resultado",
    copied: "¡Copiado!",
    seeEvents: "Ver eventos",
    playWordSearch: "Jugar la Sopa de letras",
    enter: "Enviar",
    backspace: "Borrar",
    board: "Tablero",
    selectCell: "Escribir en esta casilla",
    keyboard: "Teclado",
    states: { correct: "en su lugar", present: "en otro lugar", absent: "no está", empty: "vacía" },
  },
  en: {
    howToPlay: {
      title: "How to play Word of the day",
      items: [
        {
          q: "How do I play?",
          a: "Type a word of the given length (5 to 8 letters, it changes every day) and press Enter. Green: the letter is in the right spot. Yellow: it's in the word, but in another spot. Gray: it's not in the word. You get 6 tries.",
        },
        {
          q: "Where do the words come from?",
          a: "From this week's events in your country: concerts, fairs, theater, film and more. When you solve it, we show you the events where the word appears.",
        },
        {
          q: "When does the word change?",
          a: "Every day at midnight in your country. There's one game a day, in any language.",
        },
        {
          q: "Is there a leaderboard?",
          a: "Yes, two: by points (solving it on the first try earns 6 points, on the sixth 1) and by time (your average and your best time). To appear on it you need an account and to join the leaderboard; you can use an alias.",
        },
      ],
    },
    title: "Word of the day",
    ogSubtitle: "Guess today's word, taken from the events in your city.",
    description:
      "Guess the word of the day about events, parties and music in 6 tries. A new word every day on Ciudad Viva.",
    intro: (attempts: number) =>
      `Guess today's word about events, parties and music. You have ${attempts} tries.`,
    legend: {
      correct: "Right spot",
      present: "In the word, wrong spot",
      absent: "Not in the word",
    },
    accentsNote: "Words have 5 to 8 letters; the length changes every day.",
    loading: "Loading today's word…",
    loadError: "We couldn't load today's word.",
    retry: "Try again",
    notEnoughLetters: "Not enough letters",
    guessError: "We couldn't check your guess. Please try again.",
    expired: "The word has changed: here's today's.",
    won: (attempts: number) =>
      attempts === 1 ? "Amazing! You got it on the first try." : `Nice! You got it in ${attempts} tries.`,
    lost: [
      "So close! We know you'll get the next one.",
      "Today's word got away, but a new one is waiting for you tomorrow.",
      "No worries: the best streaks start after a tough day.",
      "Not this time. Tomorrow's your rematch!",
      "Today's was a tough one. Tomorrow brings a new chance.",
    ],
    todayWord: "Today's word:",
    wordEvents: "Today's word shows up in these events",
    notInDictionary: "Not in the word list",
    streak: (days: number) => `🔥 ${days}-day streak`,
    imported: "We moved today's game to your account.",
    playedInTitle: (language: string) => `You already played today in ${language}`,
    playedInBody: "It's one word a day, in any language. There's a new one tomorrow.",
    playedInLink: (language: string) => `See my game in ${language}`,
    synced: "Your game changed in another tab: we updated it.",
    stats: {
      title: "Your stats",
      played: "Played",
      winRate: "Win %",
      currentStreak: "Current streak",
      maxStreak: "Best streak",
      distribution: "Guess distribution",
    },
    saveStreak: {
      title: "Save your streak",
      body: "Log in to keep your games, streaks and stats in your account, including today's.",
      button: "Log in",
    },
    nextWord: "New word in",
    shareTitle: (puzzle: number) => `Word of the day #${puzzle}`,
    shareWhatsApp: "Share on WhatsApp",
    copyResult: "Copy result",
    copied: "Copied!",
    seeEvents: "See events",
    playWordSearch: "Play Word search",
    enter: "Enter",
    backspace: "Delete",
    board: "Board",
    selectCell: "Type in this box",
    keyboard: "Keyboard",
    states: { correct: "right spot", present: "wrong spot", absent: "not in the word", empty: "empty" },
  },
});
